/** Request from Wix asking for a badge for a specific event. */
export interface GetEventBadgeRequest {
  /**
   * Event for which Wix is requesting a badge.
   *
   * Contains the event ID, title, publication date, and optional ticket summary.
   */
  event?: Event;
}

/** Event for which a badge is being requested. */
export interface Event {
  /**
   * Event ID.
   * Identifies the Wix Events event being evaluated for a badge.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Event title.
   * Contains the title configured by the site owner.
   * @minLength 1
   * @maxLength 120
   */
  title?: string | null;
  /**
   * Date and time the event was published.
   * Represents the publish timestamp for the event.
   */
  publishedDate?: Date | null;
  /**
   * Summary of ticket pricing and availability for the event.
   *
   * Present only for ticketed events.
   */
  ticketsSummary?: TicketsSummary;
}

/** Snapshot of ticket pricing and availability for the event, calculated at the time of the call. */
export interface TicketsSummary {
  /**
   * Number of tickets still available for purchase across all on-sale ticket
   * definitions on the event. Unset when availability is unlimited.
   * @readonly
   */
  availableTicketsCount?: number | null;
  /**
   * Lowest ticket price for the event.
   * Calculated across all ticket definitions on the event.
   * @readonly
   */
  lowestTicketPrice?: Money;
  /**
   * Highest ticket price for the event.
   * Calculated across all ticket definitions on the event.
   * @readonly
   */
  highestTicketPrice?: Money;
  /**
   * Formatted lowest ticket price.
   * Includes the event currency for site-visitor display.
   * @maxLength 50
   * @readonly
   */
  lowestTicketPriceFormatted?: string | null;
  /**
   * Formatted highest ticket price.
   * Includes the event currency for site-visitor display.
   * @maxLength 50
   * @readonly
   */
  highestTicketPriceFormatted?: string | null;
  /**
   * Whether ticket availability is limited for this event.
   *
   * `false` when availability is unlimited or the event has no ticket definitions.
   * @readonly
   */
  hasLimitedTickets?: boolean | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
}

/** Response returned by your implementation. */
export interface GetEventBadgeResponse {
  /**
   * Badge returned for the event card.
   *
   * Omit the field, or leave it unset, when no badge applies to the event.
   */
  badge?: Badge;
}

/** Badge returned by the service plugin implementer for display on an event card. */
export interface Badge {
  /** Rendering priority used to rank badges when multiple implementers return a badge for the same event. The downstream UI selects the badge with the highest priority and uses the implementer's most recent edit time as the tie-breaker. */
  badgeType?: BadgeType;
  /**
   * Text shown on the event card. Returned as-is in whatever language the implementer chooses, with no translation by Wix.
   * @maxLength 50
   */
  text?: string | null;
}

/** Rendering priority bucket used by the storefront UI to rank multiple badges on the same event. */
export enum BadgeType {
  /** Highest priority. Use for badges that should outrank all others, such as "Sold out" or "Cancelled". */
  FIRST_PRIORITY = 'FIRST_PRIORITY',
  /** Medium priority. Use for major contextual labels, such as "Selling fast" or "Early bird". */
  SECOND_PRIORITY = 'SECOND_PRIORITY',
  /** Lowest priority. Use for nice-to-have hints, such as "New" or "Featured". */
  THIRD_PRIORITY = 'THIRD_PRIORITY',
}

/** Configuration provided when installing the Event Badges service plugin extension. */
export interface EventBadgesSpiConfig {
  /**
   * Base URI where the service plugin implementation is deployed.
   *
   * Wix appends the method path to this URI when calling the implementation.
   */
  baseUri?: SpiBaseUri;
  /**
   * List of event IDs this extension applies to.
   *
   * When empty, Wix considers the extension applicable to every event on the site.
   * @maxSize 100
   * @format GUID
   */
  eventId?: string[];
}

/** Base URI configuration for a service plugin. Wix uses these URIs to call your service plugin methods. */
export interface SpiBaseUri {
  /**
   * Base URI for your service plugin. Wix appends each method's path to this URI.
   *
   * For example, to receive requests at `https://my-app.com/v1/my-method`, set this field to `https://my-app.com/`.
   * @minLength 6
   * @maxLength 2048
   */
  baseUri?: string;
  /** Alternative URIs for specific methods. Use these to override the default URI for individual methods. */
  alternativeUris?: AlternativeUri[];
}

/** Custom URI for the specified service plugin method. */
export interface AlternativeUri {
  /**
   * Name of the method to call at the absolute URI, in PascalCase. For example, to call [Get Shipping Rates](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/shipping-rates/shipping-rates-integration-service-plugin/get-shipping-rates) on an alternative URI, specify `GetShippingRates`.
   * @minLength 3
   * @maxLength 128
   */
  methodName?: string;
  /**
   * Absolute URI that Wix calls for this method. Wix doesn't append any path to this URI.
   *
   * The URI must begin with `https://`, such as `https://my-app.com/v1/my-custom-method`.
   * @minLength 6
   * @maxLength 2048
   */
  absoluteUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Extension ID in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionId?: string | null;
  /**
   * Extension type in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionType?: string | null;
  /**
   * Invoked function.
   * @internal
   * @maxLength 256
   */
  functionName?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
